import React, { useMemo } from "react";
import { Typography } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { SbTable } from "uiCore";
import { BackLinkStyled } from "../../RoleDetailV2.styles";
import { DetailHeadingRowStyled, PlanTableWrapperStyled } from "../ViewPlanningV2.styles";
import { useWorkforcePeopleAvailability } from "../../useTalentMobilityData";
import { useWorkforceJobCandidateColumns } from "../useViewPlanningColumns";
import { tableEmptyLocale } from "../ViewPlanningV2.utils";
import { IndexInfoLabel } from "../../components/IndexTooltip";

const { Title, Text } = Typography;

// The selected workforce job's shortlisted people — only the saved plan's
// shortlisted_users are listed (the plan mirror, like the redeployment
// drill-down's available-only table), in the same ranked-table format:
// SMI-ranked rows with availability answered by
// GET /talent-mobility/workforce-planning/people-availability.
const WorkforceJobDetail = ({ selectedPlan, selectedJob, onBack }) => {
  const availabilityRole = useMemo(() => ({ id: selectedJob.jd_sb_unique_id }), [selectedJob]);
  const { candidates, isLoading } = useWorkforcePeopleAvailability(availabilityRole);
  const shortlistedIds = useMemo(
    () => new Set(selectedJob.shortlisted_users || []),
    [selectedJob]
  );
  const shortlistedCandidates = useMemo(
    () =>
      candidates
        .filter((candidate) => shortlistedIds.has(candidate.personId))
        // Null (not yet scored) sorts last, same convention as
        // RedeploymentCenterV2.utils' buildScoreColumn default sorter —
        // `b.sri - a.sri` alone would produce NaN once sri can be null.
        .sort((a, b) => (b.sri ?? -1) - (a.sri ?? -1)),
    [candidates, shortlistedIds]
  );

  const columns = useWorkforceJobCandidateColumns(selectedJob.jd_sb_unique_id, shortlistedCandidates);

  // Fragment root so the tab pane's flex column (TabsBodyStyled) can
  // hand the remaining height to the table wrapper, which owns the scroll.
  return (
    <>
      <BackLinkStyled onClick={onBack}>
        <ArrowLeftOutlined /> Back to Jobs
      </BackLinkStyled>
      <DetailHeadingRowStyled>
        <Title level={5}>
          {selectedPlan.plan_name} — {selectedJob.title || selectedJob.jd_sb_unique_id} (
          {selectedJob.current_position ?? 0} → {selectedJob.target_position ?? 0})
        </Title>
      </DetailHeadingRowStyled>
      <Text type="secondary">
        People shortlisted for this job in this plan, ranked by <IndexInfoLabel index="SMI" />.
      </Text>

      <PlanTableWrapperStyled>
        <SbTable
          columns={columns}
          dataSource={shortlistedCandidates}
          rowKey={(record) => record.personId}
          sticky
          locale={tableEmptyLocale(
            isLoading ? null : shortlistedCandidates,
            "No people shortlisted for this job in this plan."
          )}
        />
      </PlanTableWrapperStyled>
    </>
  );
};

export default WorkforceJobDetail;
